import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { tasksApi } from '@/lib/api/tasks'
import type { TaskKMCodesResponse, TaskSummary } from '@/types'

interface TaskCountersWSMessage {
  type: string
  codes_count: number
}

/** Live KM counter updates via WebSocket on admin task detail. */
export function useTaskCountersWebSocket(taskUuid: string | undefined) {
  const qc = useQueryClient()
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    if (!taskUuid) {
      setConnected(false)
      return
    }

    let ws: WebSocket | null = null
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    let invalidateTimer: ReturnType<typeof setTimeout> | undefined
    let closed = false

    const scheduleCodesRefresh = () => {
      if (invalidateTimer) clearTimeout(invalidateTimer)
      invalidateTimer = setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['task-km-codes', taskUuid] })
      }, 1500)
    }

    const applyCodesCount = (count: number) => {
      qc.setQueryData(['task', taskUuid], (old: TaskSummary | undefined) => {
        if (!old) {
          void qc.invalidateQueries({ queryKey: ['task', taskUuid] })
          return old
        }
        if (count <= old.codes_count) return old
        return { ...old, codes_count: count }
      })
      qc.setQueriesData<TaskSummary[]>(
        { queryKey: ['tasks'] },
        old =>
          old?.map(t =>
            t.uuid === taskUuid ? { ...t, codes_count: Math.max(t.codes_count, count) } : t,
          ),
      )
      qc.setQueriesData<TaskKMCodesResponse>(
        { queryKey: ['task-km-codes', taskUuid] },
        old => {
          if (!old || count <= old.total) return old
          return { ...old, total: count }
        },
      )
      scheduleCodesRefresh()
    }

    const connect = () => {
      ws = new WebSocket(tasksApi.countersWebSocketUrl(taskUuid))
      ws.onopen = () => setConnected(true)
      ws.onclose = () => {
        setConnected(false)
        if (!closed) {
          retryTimer = setTimeout(connect, 2000)
        }
      }
      ws.onmessage = ev => {
        try {
          const msg = JSON.parse(String(ev.data)) as TaskCountersWSMessage
          if (msg.type !== 'counters' || typeof msg.codes_count !== 'number') return
          applyCodesCount(msg.codes_count)
        } catch {
          // ignore malformed frames
        }
      }
    }

    connect()
    return () => {
      closed = true
      if (retryTimer) clearTimeout(retryTimer)
      if (invalidateTimer) clearTimeout(invalidateTimer)
      ws?.close()
      setConnected(false)
    }
  }, [taskUuid, qc])

  return connected
}
